import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import useAutoRefresh from '../hooks/useAutoRefresh';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  CalendarPlus, CalendarMinus, Eye, EyeOff, Minus, Plus,
  UserPlus, X, Settings2, Briefcase,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Event Calendar day panel — full admin control over each event day:
// quantity (down to 0 / overfill allowed), publish/hide, add & remove staff,
// add & remove days. Reuses the existing Upcoming Shifts endpoints.
export const EventDayManagePanel = ({ dateStr, onChanged }) => {
  const navigate = useNavigate();
  const [days, setDays] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [addingFor, setAddingFor] = useState(null);
  const [staffSearch, setStaffSearch] = useState('');
  const [newDayFor, setNewDayFor] = useState(null);
  const [newDayDate, setNewDayDate] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/open-shifts?include_full=true`);
      const list = (res.data || []).filter(
        (o) => o.date === dateStr && (o.shift_category || 'event') !== 'retail'
      );
      setDays(list);
    } catch (_e) { /* silent */ }
  }, [dateStr]);

  useEffect(() => { load(); }, [load]);

  // Keep the day panel in sync across admins without manual refresh.
  useAutoRefresh(load);

  useEffect(() => {
    axios.get(`${API}/admin/employees`).then((r) => {
      const d = r.data;
      setEmployees(Array.isArray(d) ? d : d.employees || []);
    }).catch(() => {});
  }, []);

  const refresh = async () => { await load(); onChanged && onChanged(); };

  const setQty = async (day, qty) => {
    if (qty < 0) return;
    try {
      await axios.post(`${API}/open-shifts/${day.id}/edit-day-capacity`, { quantity: qty });
      toast.success(`Capacity set to ${qty}`);
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update capacity'); }
  };

  const toggleVisibility = async (day) => {
    try {
      const res = await axios.post(`${API}/open-shifts/${day.id}/toggle-visibility?whole_event=true`);
      toast.success(res.data.hidden ? 'Event hidden from employees' : 'Event published — employees can now claim');
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const addStaff = async (day, userId) => {
    try {
      await axios.post(`${API}/open-shifts/${day.id}/add-staff`, { user_id: userId });
      toast.success('Staff added');
      setAddingFor(null); setStaffSearch('');
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add staff'); }
  };

  const removeStaff = async (day, userId, wholeEvent) => {
    try {
      await axios.post(`${API}/open-shifts/${day.id}/remove-staff?single_day=${!wholeEvent}&user_id=${userId}`);
      toast.success(wholeEvent ? 'Removed from whole event' : 'Removed from this day');
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to remove'); }
  };

  const addDay = async (day) => {
    if (!newDayDate) { toast.error('Pick a date first'); return; }
    try {
      await axios.post(`${API}/open-shifts/${day.id}/duplicate`, [newDayDate]);
      toast.success(`Day ${newDayDate} added to ${day.location}`);
      setNewDayFor(null); setNewDayDate('');
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add day'); }
  };

  const removeDay = async (day) => {
    const staffCount = (day.claimed_by_list || []).length;
    const warn = staffCount > 0
      ? `Remove ${day.date} from "${day.location}"?\n\n${staffCount} staff member(s) are assigned to this day — they will be unassigned and their scheduled shifts for this day deleted. Worked/clocked shifts are preserved in history.`
      : `Remove ${day.date} from "${day.location}"?`;
    if (!window.confirm(warn)) return;
    try {
      await axios.delete(`${API}/open-shifts/${day.id}?force=true`);
      toast.success(`Day ${day.date} removed from ${day.location}`);
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to remove day'); }
  };

  if (!days.length) return null;

  return (
    <Card className="bg-card border-primary/40 rounded-sm" data-testid="event-day-manage-panel">
      <CardHeader className="border-b border-border pb-3">
        <CardTitle className="text-xs font-barlow tracking-wider uppercase flex items-center gap-2">
          <Briefcase className="w-4 h-4 text-primary" /> Manage events on this day
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 space-y-4">
        {days.map((day) => {
          const staff = day.claimed_by_list || [];
          const qty = day.quantity || 0;
          const filtered = employees.filter((e) =>
            (e.name || '').toLowerCase().includes(staffSearch.toLowerCase())
            && !staff.some((c) => c.user_id === e.id)).slice(0, 6);
          return (
            <div key={day.id} className="border border-border rounded-sm p-3 space-y-3" data-testid={`event-day-${day.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                  <p className="font-semibold text-sm truncate">{day.location} <span className="text-muted-foreground font-normal">· {day.position}</span></p>
                  <p className="text-xs text-muted-foreground">{day.start_time || '--'} - {day.end_time || '--'} · {staff.length}/{qty} staff
                    {staff.length > qty && <span className="text-amber-400"> (over-staffed)</span>}
                  </p>
                </div>
                {day.hidden
                  ? <Badge variant="outline" className="rounded-sm text-[10px] uppercase text-red-400 border-red-500/50">Hidden</Badge>
                  : <Badge className="rounded-sm text-[10px] uppercase bg-green-600 text-white">Public</Badge>}
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs uppercase tracking-wider text-muted-foreground">Slots:</span>
                <Button size="sm" variant="outline" className="h-7 w-7 p-0 rounded-sm" onClick={() => setQty(day, qty - 1)} data-testid={`qty-minus-${day.id}`}><Minus className="w-3 h-3" /></Button>
                <span className="text-sm font-semibold w-6 text-center" data-testid={`qty-value-${day.id}`}>{qty}</span>
                <Button size="sm" variant="outline" className="h-7 w-7 p-0 rounded-sm" onClick={() => setQty(day, qty + 1)} data-testid={`qty-plus-${day.id}`}><Plus className="w-3 h-3" /></Button>
                <Button size="sm" variant="outline" className="h-7 rounded-sm text-[10px] uppercase" onClick={() => toggleVisibility(day)} data-testid={`toggle-vis-${day.id}`}>
                  {day.hidden ? <><Eye className="w-3 h-3 mr-1" /> Publish</> : <><EyeOff className="w-3 h-3 mr-1" /> Hide</>}
                </Button>
                <Button size="sm" variant="outline" className="h-7 rounded-sm text-[10px] uppercase" onClick={() => { setNewDayFor(newDayFor === day.id ? null : day.id); setNewDayDate(''); }} data-testid={`add-day-btn-${day.id}`}>
                  <CalendarPlus className="w-3 h-3 mr-1" /> Add day
                </Button>
                <Button size="sm" variant="outline" className="h-7 rounded-sm text-[10px] uppercase text-red-400" onClick={() => removeDay(day)} data-testid={`remove-day-btn-${day.id}`}>
                  <CalendarMinus className="w-3 h-3 mr-1" /> Remove day
                </Button>
                <Button size="sm" variant="outline" className="h-7 rounded-sm text-[10px] uppercase" onClick={() => navigate(`/admin/events/view?name=${encodeURIComponent(day.location || '')}`)} data-testid={`full-edit-${day.id}`}>
                  <Settings2 className="w-3 h-3 mr-1" /> Full edit
                </Button>
              </div>

              {newDayFor === day.id && (
                <div className="flex items-center gap-2">
                  <Input type="date" value={newDayDate} onChange={(e) => setNewDayDate(e.target.value)} className="h-8 rounded-sm w-44" data-testid={`new-day-date-${day.id}`} />
                  <Button size="sm" className="h-8 rounded-sm bg-primary text-primary-foreground text-xs uppercase" onClick={() => addDay(day)} data-testid={`confirm-add-day-${day.id}`}>Add</Button>
                </div>
              )}

              <div className="space-y-1">
                {staff.map((c) => (
                  <div key={c.user_id} className="flex items-center justify-between gap-2 text-sm border-b border-border/40 pb-1">
                    <span className="truncate">{c.user_name}</span>
                    <span className="flex gap-1 shrink-0">
                      <Button size="sm" variant="ghost" className="h-6 px-2 rounded-sm text-[10px] uppercase text-amber-400" onClick={() => removeStaff(day, c.user_id, false)} data-testid={`remove-day-staff-${day.id}-${c.user_id}`}>This day</Button>
                      <Button size="sm" variant="ghost" className="h-6 px-2 rounded-sm text-[10px] uppercase text-red-400" onClick={() => removeStaff(day, c.user_id, true)} data-testid={`remove-event-staff-${day.id}-${c.user_id}`}><X className="w-3 h-3 mr-0.5" /> Event</Button>
                    </span>
                  </div>
                ))}
                {addingFor === day.id ? (
                  <div className="space-y-1 pt-1">
                    <Input placeholder="Search employee…" value={staffSearch} onChange={(e) => setStaffSearch(e.target.value)} className="h-8 rounded-sm" data-testid={`staff-search-${day.id}`} />
                    {filtered.map((e) => (
                      <button key={e.id} className="w-full text-left text-sm px-2 py-1 hover:bg-muted rounded-sm" onClick={() => addStaff(day, e.id)} data-testid={`pick-staff-${day.id}-${e.id}`}>
                        {e.name}
                      </button>
                    ))}
                  </div>
                ) : (
                  <Button size="sm" variant="outline" className="h-7 rounded-sm text-[10px] uppercase mt-1" onClick={() => { setAddingFor(day.id); setStaffSearch(''); }} data-testid={`add-staff-btn-${day.id}`}>
                    <UserPlus className="w-3 h-3 mr-1" /> Add staff
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default EventDayManagePanel;
